import { lazy, Suspense, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Link, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppShell, Banner, LinkProvider, Spinner, TooltipHost } from "@jkast90/kast-common/ui";
import { SessionProvider, useSession } from "@jkast90/kast-common/auth";
import { useCacheScopedToUser } from "@jkast90/kast-common/query-client";
import { queryClient } from "@common/queryClient";
import AppErrorBoundary from "@common/components/AppErrorBoundary";
import NavRecorder from "@common/components/NavRecorder";
import SiteFooter from "@common/components/SiteFooter";
import HomePage from "./home/pages/HomePage";
import { AppSettings } from "./account/kit";
import { AdminOnly, SignedIn } from "./auth/components/AuthGates";
import { session } from "./auth/session";

const AdminPanel = lazy(() => import("@admin/pages/AdminPanel"));
const AccountPage = lazy(() => import("./account/pages/AccountPage"));
const Resume = lazy(() => import("./resume/pages/Resume"));
const PrivacyPolicy = lazy(() => import("./legal/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./legal/pages/TermsOfService"));
const DeleteAccount = lazy(() => import("./legal/pages/DeleteAccount"));
const OtherPage = lazy(() => import("./other/pages/OtherPage"));
const BugReportPage = lazy(() => import("./bug-report/pages/BugReportPage"));

/** Keyed on the section (/admin, /resume, ...), so moving to another section
 * clears a crash while switching admin tabs doesn't remount the panel. Each page
 * renders its own header, so AdminPanel has a boundary of its own to keep its
 * tabs usable. */
function CrashScreen({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <AppErrorBoundary key={location.pathname.split("/")[1]}>{children}</AppErrorBoundary>;
}

/** Drops the cache whenever someone else signs in (or out). */
function CacheScope() {
  useCacheScopedToUser(queryClient, useSession().user?.sub || null);
  return null;
}

export default function App() {
  return (
    <SessionProvider session={session}>
      <QueryClientProvider client={queryClient}>
        <CacheScope />
        <BrowserRouter>
          <LinkProvider link={Link}>
            <NavRecorder />
            <Banner placement="top-end" max={3} />
            <TooltipHost />
            <AppSettings />
            <AppShell footer={<SiteFooter />}>
              <CrashScreen>
                <Suspense fallback={<Spinner />}>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/admin" element={<Navigate to="/admin/sites" replace />} />
                    <Route
                      path="/admin/:tab"
                      element={
                        <AdminOnly>
                          <AdminPanel />
                        </AdminOnly>
                      }
                    />
                    <Route path="/account" element={<AccountPage />} />
                    <Route path="/resume" element={<Resume />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/delete-account" element={<DeleteAccount />} />
                    <Route path="/bug-report" element={<BugReportPage />} />
                    <Route
                      path="/other"
                      element={
                        <SignedIn>
                          <OtherPage />
                        </SignedIn>
                      }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </CrashScreen>
            </AppShell>
          </LinkProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </SessionProvider>
  );
}
